import * as React from 'react';
import * as styles from './styles.scss';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { IconButton, IButtonStyles } from '@fluentui/react/lib/Button';
import type {
    IContextualMenuProps,
    IContextualMenuItemStyles,
} from '@fluentui/react/lib/ContextualMenu';
import type { IIconProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { ControlIcons } from 'owa-control-icons';
import { renderDueDateMenuPickerAsCallout } from 'owa-timestream-asset-sdk-duedates';
import { isCommandButton, getAssetCommands } from '../assetCommands';
import loc from 'owa-localize';
import useDeleteAsset from '../hooks/useDeleteAsset';
import { useAssetId } from '../hooks/useAssetId';
import { useProjectId } from '../hooks/useProjectId';
import { optionsText } from './AssetOptions.locstring.json';

const iconProps: IIconProps = {
    iconName: ControlIcons.More,
    styles: {
        root: styles.assetEllipsisIcon,
    },
};

const dropdownIconProps: IIconProps = {
    className: styles.assetEllipsisMenuButton,
};

const menuItemDisabled: Partial<IContextualMenuItemStyles> = {
    linkContent: styles.menuItemDisabled,
};

const menuItemIconStyles: IIconStyles = {
    root: styles.menuItemIcon,
};

const menuItemIconDisabledStyles: IIconStyles = {
    root: styles.menuItemDisabled,
};

export default observer(function AssetOptions({
    assetRef,
    top = 17,
    right = 12,
    shouldHideOptions,
    onOptionsOpened,
    onOptionsClosed,
}: {
    assetRef: React.RefObject<HTMLDivElement>;
    top?: number;
    right?: number;
    shouldHideOptions?: boolean;
    onOptionsOpened?: () => void;
    onOptionsClosed?: () => void;
}) {
    const assetId = useAssetId();
    const projectId = useProjectId();
    const deleteAsset = useDeleteAsset();

    const [menuIsOpen, setMenuIsOpen] = React.useState(false);
    const onMenuOpened = React.useCallback(() => {
        // Input fields paired with menu buttons can automatically attempt to regain focus
        // (which closes the menu immediately), so manually set focus on menu when it opens
        assetRef?.current?.focus();

        setMenuIsOpen(true);
        onOptionsOpened?.();
    }, [onOptionsOpened]);
    const onMenuDismissed = React.useCallback(() => {
        setMenuIsOpen(false);
        onOptionsClosed?.();
    }, [onOptionsClosed]);

    const assetCommands = menuIsOpen ? getAssetCommands(projectId, [assetId], deleteAsset) : [];

    const items = assetCommands.map(command => {
        const isDisabled = command.disabled;
        const baseOption = {
            key: command.id,
            text: loc(command.titleResourceId),
            iconProps: {
                iconName: command.iconName,
                styles: isDisabled ? menuItemIconDisabledStyles : menuItemIconStyles,
            },
            className: styles.menuItem,
            itemProps: { styles: isDisabled ? menuItemDisabled : {} },
            disabled: isDisabled,
        };

        if (isCommandButton(command)) {
            return {
                ...baseOption,
                onClick: command.onInvoke,
            };
        } else {
            return {
                ...baseOption,
                subMenuProps: {
                    id: command.id,
                    items: command.getMenuItems(),
                },
            };
        }
    });

    const buttonStyles: IButtonStyles = {
        root: { top, right },
    };

    const menuProps: IContextualMenuProps = {
        items,
        onMenuOpened,
        onMenuDismissed,
        // VSO 105566 - calendars are not accessible when rendered in a contextual menu,
        // so override the submenu function to render it as a callout instead
        onRenderSubMenu: renderDueDateMenuPickerAsCallout,
    };

    return (
        <IconButton
            className={classNames(styles.assetEllipsisButton, {
                [styles.assetEllipsisHidden]: !!shouldHideOptions,
            })}
            menuIconProps={dropdownIconProps}
            iconProps={iconProps}
            menuProps={menuProps}
            styles={buttonStyles}
            title={loc(optionsText)}
        />
    );
});
